import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeft, Lock } from 'lucide-react';
import { Alert, AlertDescription } from '../../ui/alert';
import { FunnelStepper } from '../../ui/funnel-stepper';
import { Badge } from '../../ui/badge';
import { useDiagnosticStore } from '../../../stores/diagnostic';
import { useStoredAudienceId } from '../../../hooks/useActiveAudience';
import { getAudienceById, type AudienceId } from '@shared/constants/audiences';
import { easeStripe } from '../../../lib/motion';
import {
  trackFunnelStart,
  trackStepView,
  trackFunnelAbandon,
  type FunnelContext,
} from '../../../lib/tracking';
import { useDraftAutosave } from '../../../hooks/useDraftAutosave';
import { AUDIENCE_TO_PROFILE, STEPPER_STEPS, type ProfileId } from './types';
import { Step1Profil } from './Step1Profil';
import { Step2Siret } from './Step2Siret';
import { Step3Questions } from './Step3Questions';
import { Step4Lead } from './Step4Lead';
import { Step5Done } from './Step5Done';

type LocalStep = 'profile' | 'siret' | 'questions' | 'lead' | 'estimation' | 'done';

/**
 * Calcule l'étape de départ depuis le state persisté du store.
 * Permet de reprendre le funnel après un F5 sans bloquer l'utilisateur à
 * l'écran "profile" alors que le SIRET et les réponses sont déjà saisis.
 */
function deriveInitialStep(store: {
  leadId: string | null;
  estimation: unknown;
  questions: unknown[];
  siret: string;
}): LocalStep {
  if (store.leadId) return 'done';
  if (store.estimation) return 'lead';
  if (store.questions.length > 0) return 'questions';
  if (store.siret) return 'siret';
  return 'profile';
}

function deriveInitialProfile(
  store: { siret: string; profileId?: string | null },
  audience: string | null,
): ProfileId | null {
  // 1. Profil explicitement persisté dans le store (F5 safe)
  if (store.profileId) return store.profileId as ProfileId;
  // 2. Profil dérivé depuis l'audience stockée
  if (audience) {
    const auto = AUDIENCE_TO_PROFILE[audience as keyof typeof AUDIENCE_TO_PROFILE];
    if (auto) return auto;
  }
  // 3. Fallback "professionnel" si on reprend un funnel en cours sans profil connu
  if (store.siret) return 'professionnel';
  return null;
}

interface DiagnosticFunnelProps {
  /**
   * Verrouille le profil sur cette audience.
   * Quand fourni, l'étape de sélection profil est skippée et
   * le profil est pré-rempli depuis `AUDIENCE_TO_PROFILE`.
   */
  lockedAudience?: AudienceId;
}

export function DiagnosticFunnel({ lockedAudience }: DiagnosticFunnelProps = {}) {
  const store = useDiagnosticStore();
  const storedAudience = useStoredAudienceId();
  const audience = lockedAudience ?? storedAudience;

  // Profil dérivé du verrou d'audience (priorité max)
  const lockedProfile = lockedAudience
    ? (AUDIENCE_TO_PROFILE[lockedAudience] ?? null)
    : null;

  // Metadata de l'audience verrouillée (pour le badge visuel)
  const lockedAudienceMeta = lockedAudience ? getAudienceById(lockedAudience) : null;

  const [localStep, setLocalStep] = useState<LocalStep>(() => {
    // Audience verrouillée avec profil dérivable et funnel vierge → skip Step1
    if (lockedProfile && !store.leadId && !store.estimation && !store.questions.length && !store.siret) {
      return 'siret';
    }
    return deriveInitialStep(store);
  });
  const [profile, setProfile] = useState<ProfileId | null>(() =>
    lockedProfile ?? deriveInitialProfile(store, audience),
  );
  const [dir, setDir] = useState(1);

  const DIAG_STEP_INDEX: Record<string, number> = {
    profile: 0,
    siret: 1,
    questions: 2,
    lead: 3,
    estimation: 3,
    done: 4,
  };

  // Autosave serveur du draft (progression cross-device)
  useDraftAutosave('diagnostic', {
    step: localStep,
    stepIndex: DIAG_STEP_INDEX[localStep] ?? 0,
    payload: {
      siret: store.siret,
      segment: store.segment,
      profileId: store.profileId,
      answersCount: store.questions.length,
    },
    stepsCompleted: Object.keys(DIAG_STEP_INDEX).filter(
      (s) => (DIAG_STEP_INDEX[s] ?? 0) < (DIAG_STEP_INDEX[localStep] ?? 0),
    ),
    intent: 'cee',
    source: 'diagnostic',
    audience: lockedAudience,
    disabled: !!store.leadId,
  });

  // Quand le profil est verrouillé, s'assurer que le store le reflète
  useEffect(() => {
    if (lockedProfile && store.profileId !== lockedProfile) {
      store.setProfileId(lockedProfile);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lockedProfile]);

  // Pré-remplir le profil quand l'audience stockée change (sélection via le
  // Header/Dropdown). Ignoré si le profil est verrouillé par la page.
  useEffect(() => {
    if (lockedAudience || !audience || localStep !== 'profile') return;
    const autoProfile = AUDIENCE_TO_PROFILE[audience];
    if (autoProfile) {
      setProfile(autoProfile);
      setLocalStep('siret');
    }
  }, [audience, localStep, lockedAudience]);

  // Synchronisation store.leadId → étape "done" (utile si setLead est appelé
  // ailleurs, ou pour reprendre un funnel terminé après F5).
  useEffect(() => {
    if (store.leadId && localStep !== 'done') {
      setDir(1);
      setLocalStep('done');
    }
  }, [store.leadId, localStep]);

  const diagCtx: FunnelContext = {
    funnelId: 'diagnostic',
    intent: 'cee',
    source: 'diagnostic',
    audience: lockedAudience,
  };

  // Tracking PostHog : funnel_start au montage, step_view à chaque changement d'étape.
  const didStart = useRef(false);
  useEffect(() => {
    if (!didStart.current) {
      didStart.current = true;
      if (!store.leadId) {
        trackFunnelStart(diagCtx);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    trackStepView(diagCtx, localStep, DIAG_STEP_INDEX[localStep] ?? 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localStep]);

  // Abandon au déchargement
  useEffect(() => {
    const onUnload = () => {
      if (localStep !== 'done') {
        trackFunnelAbandon(diagCtx, localStep, DIAG_STEP_INDEX[localStep] ?? 0);
      }
    };
    window.addEventListener('pagehide', onUnload);
    return () => window.removeEventListener('pagehide', onUnload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localStep]);

  // Scroll to top du funnel à chaque changement d'étape (mobile : step suivant hors écran sinon)
  // + focus du contenu de l'étape après la transition (a11y clavier/SR).
  // On saute le premier rendu pour ne pas voler le scroll/focus au chargement.
  const skippedFirstScroll = useRef(false);
  useEffect(() => {
    if (!skippedFirstScroll.current) {
      skippedFirstScroll.current = true;
      return;
    }
    document
      .getElementById('diagnostic-funnel-top')
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    const t = window.setTimeout(() => {
      document.getElementById('diagnostic-step-content')?.focus({ preventScroll: true });
    }, 300);
    return () => window.clearTimeout(t);
  }, [localStep]);

  const stepIdx =
    localStep === 'profile' ? 0
    : localStep === 'siret' ? 1
    : localStep === 'questions' ? 2
    : localStep === 'lead' || localStep === 'estimation' ? 3
    : localStep === 'done' ? 4
    : 0;

  // Première étape accessible : si profil verrouillé, siret ; sinon profile
  const firstStep: LocalStep = lockedProfile ? 'siret' : 'profile';
  const canGoBack = localStep !== firstStep && localStep !== 'done';

  return (
    <div
      id="diagnostic-funnel-top"
      className="relative overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-[0_8px_32px_rgba(0,0,0,.07)]"
    >

      {/* ── Progress header ── */}
      <div className="border-b border-stone-200 bg-stone-50/70 px-5 py-4 md:px-8">
        {lockedAudienceMeta && (
          <div className="mb-3 inline-flex items-center gap-1.5 rounded-md bg-stone-100 px-2 py-1 text-[11px] font-semibold text-stone-600">
            <Lock className="h-2.5 w-2.5" strokeWidth={2.5} />
            Parcours {lockedAudienceMeta.shortLabel}
          </div>
        )}
        <FunnelStepper steps={STEPPER_STEPS} current={stepIdx} />
      </div>

      {/* ── Company badge ── */}
      {store.companyName && localStep !== 'profile' && (
        <div className="border-b border-stone-100 bg-stone-50/40 px-5 py-2 md:px-8">
          <Badge variant="outline" className="text-[11px]">
            {store.companyName}
          </Badge>
        </div>
      )}

      {/* ── Error ── */}
      {store.error && (
        <div className="px-5 pt-4 md:px-8">
          <Alert variant="error" className="rounded-xl py-2.5">
            <AlertDescription className="text-[13px]">{store.error}</AlertDescription>
          </Alert>
        </div>
      )}

      {/* ── Body ── */}
      <div className="relative px-5 py-6 md:px-8 md:py-8">
        {canGoBack && (
          <button
            type="button"
            onClick={() => {
              setDir(-1);
              store.setError(null);
              if (localStep === 'lead' || localStep === 'estimation') setLocalStep('questions');
              else if (localStep === 'questions') setLocalStep('siret');
              else if (localStep === 'siret') setLocalStep(firstStep);
            }}
            className="-ml-1 mb-5 inline-flex min-h-11 items-center gap-1 rounded-md px-2 py-2 text-[12px] text-stone-400 transition-colors hover:bg-stone-50 hover:text-stone-700"
          >
            <ChevronLeft className="h-3.5 w-3.5" />
            Retour
          </button>
        )}

        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={localStep}
            id="diagnostic-step-content"
            tabIndex={-1}
            className="outline-none"
            initial={{ opacity: 0, x: dir * 24 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: dir * -24 }}
            transition={{ duration: 0.28, ease: easeStripe }}
          >
            {localStep === 'profile' && (
              <Step1Profil
                onSelect={(p) => {
                  setDir(1);
                  setProfile(p);
                  store.setProfileId(p);
                  setLocalStep('siret');
                }}
              />
            )}
            {localStep === 'siret' && (
              <Step2Siret
                profile={profile!}
                onNext={() => {
                  setDir(1);
                  setLocalStep('questions');
                }}
              />
            )}
            {localStep === 'questions' && (
              <Step3Questions
                onNext={() => {
                  setDir(1);
                  setLocalStep('lead');
                }}
              />
            )}
            {(localStep === 'lead' || localStep === 'estimation') && <Step4Lead />}
            {localStep === 'done' && <Step5Done />}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}
